import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageCircle, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';

interface StartupComment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  profiles: {
    username: string;
    avatar_url: string | null;
  };
}

interface StartupCommentsProps {
  startupId: string;
}

export const StartupComments = ({ startupId }: StartupCommentsProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [newComment, setNewComment] = useState('');

  const { data: comments, refetch } = useQuery({
    queryKey: ['startup-comments', startupId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('startup_comments')
        .select(`
          *,
          profiles (username, avatar_url)
        `)
        .eq('startup_id', startupId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as StartupComment[];
    }
  });

  const handleSubmitComment = async () => {
    if (!user || !newComment.trim()) return;

    try {
      const { error } = await supabase
        .from('startup_comments')
        .insert({
          startup_id: startupId,
          content: newComment,
          user_id: user.id
        });

      if (error) throw error;

      toast({
        title: "Comment posted",
        description: "Your comment has been posted successfully.",
      });

      setNewComment('');
      refetch();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to post comment. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="bg-card border border-flux-cyan/10 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-6">
        <MessageCircle className="h-5 w-5" />
        <h2 className="text-xl font-bold">Comments</h2>
      </div>

      {user && (
        <div className="mb-6">
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Write a comment..."
            className="mb-2 bg-background border-flux-cyan/20 focus-visible:ring-flux-cyan/30"
          />
          <Button 
            onClick={handleSubmitComment}
            className="bg-white hover:bg-corporate-gray-100 text-corporate-black"
          >
            <Send className="h-4 w-4 mr-2" />
            Post Comment
          </Button>
        </div>
      )}

      <div className="space-y-6">
        {comments?.map((comment) => (
          <div key={comment.id} className="flex gap-4">
            <Avatar>
              <AvatarImage src={comment.profiles.avatar_url || ''} />
              <AvatarFallback>{comment.profiles.username[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium">{comment.profiles.username}</span>
                <span className="text-sm text-muted-foreground">
                  {new Date(comment.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-sm text-muted-foreground">{comment.content}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};